/**
 * System APIs for Electron
 * Handles app version, notifications, history, auto-start, and system info
 */

import {
  ExternalLinkRequestedPayloadSchema,
  type InstallHealthStatus,
  type InstallRepairResult,
  IPCEventType,
  type QuitSource,
  type UpdateActivityReport,
  type UpdateChannel,
  UpdateEventType,
  type UpdateResult,
  type UpdateStatus,
} from '@electron/shared';
import { APP_LAUNCH_URLS } from '@electron/shared/app-launch-urls';
import {
  BACKEND_CHOICE_CHANGED_CHANNEL,
  BACKEND_CHOICE_GET_CHANNEL,
  BACKEND_CHOICE_SET_CHANNEL,
  type BackendChoice,
  BackendChoicePayloadSchema,
  BackendChoiceSchema,
} from '@electron/shared/backend-choice.types';
import { type InstalledApp, type LogFileInfo } from '@electron/shared/types';
import { head } from 'lodash';

import { ElectronAPI } from '../base/ElectronAPI';

import type { ElectronListenerMethod } from '../base/types';

export class SystemAPI extends ElectronAPI {
  /**
   * Get app version - handled by Electron main process
   */
  async getAppVersion() {
    return this.invoke<string>('get-app-version');
  }

  /**
   * Relaunch app - handled by Electron main process
   */
  async relaunchApp() {
    return this.invoke('relaunch-app', {});
  }

  /**
   * Get device ID - handled by Electron main process
   */
  async getDeviceId() {
    return this.invoke<string>('get-device-id', {});
  }

  /** Main's current choice, which it may have restored from an earlier launch */
  async getBackendChoice() {
    return BackendChoiceSchema.parse(
      await this.invoke<unknown>(BACKEND_CHOICE_GET_CHANNEL)
    );
  }

  /** Main routes its own and ContextKit's backend requests by this choice */
  async setBackendChoice(backendChoice: BackendChoice) {
    return this.invoke<void>(BACKEND_CHOICE_SET_CHANNEL, { backendChoice });
  }

  /** Main's choice changed, from this window or another, or from main itself */
  listenForBackendChoiceChange(
    callback: (backendChoice: BackendChoice) => void
  ) {
    return this.addListener({
      eventType: BACKEND_CHOICE_CHANGED_CHANNEL,
      callback: ({ backendChoice }) => callback(backendChoice),
      payloadValidator: BackendChoicePayloadSchema,
    });
  }

  /**
   * Get platform - returns 'macos' or 'windows'
   */
  async getPlatform(): Promise<'macos' | 'windows'> {
    return this.invoke<'macos' | 'windows'>('get-platform');
  }

  /**
   * Get this device's base slug (derived from the hostname) - handled by Electron main process
   */
  async getDeviceSlug() {
    return this.invoke<string>('get-device-slug', {});
  }

  /**
   * Get session ID - handled by Electron main process
   */
  async getSessionId() {
    return this.invoke<string>('get-session-id');
  }

  /**
   * Enable auto-start - handled by Electron main process
   */
  async enableAutoStart(): Promise<boolean> {
    return this.invoke<boolean>('enable-auto-start');
  }

  /**
   * Disable auto-start - handled by Electron main process
   */
  async disableAutoStart(): Promise<boolean> {
    return this.invoke<boolean>('disable-auto-start');
  }

  /**
   * Get auto-start status - handled by Electron main process
   */
  async getAutoStartStatus(): Promise<boolean> {
    return this.invoke<boolean>('get-auto-start-status');
  }

  /**
   * Get current window info from system API
   */
  getCurrentWindow() {
    return window.systemInfo?.currentWindow;
  }

  listenForExternalLinkRequest: ElectronListenerMethod<IPCEventType.externalLinkRequested> =
    (callback, onError, onUnlisten, timeout) =>
      this.addListener({
        eventType: IPCEventType.externalLinkRequested,
        callback,
        onError,
        onUnlisten,
        timeout,
        payloadValidator: ExternalLinkRequestedPayloadSchema,
      });

  openExternal(
    url: string,
    options?: { forceInBrowser?: boolean; preferMeetingApp?: boolean }
  ): Promise<void> {
    try {
      const parsed = new URL(url);
      const allowed = new Set([
        'http:',
        'https:',
        'mailto:',
        'imessage:',
        'slack:',
      ]);
      const isSlackDm =
        parsed.protocol === 'slack:' && parsed.hostname === 'user';
      if (
        !APP_LAUNCH_URLS.has(parsed.href) &&
        (!allowed.has(parsed.protocol) ||
          (parsed.protocol === 'slack:' && !isSlackDm))
      ) {
        return Promise.reject(
          new Error(`Unsupported protocol: ${parsed.protocol}`)
        );
      }
    } catch (_err) {
      return Promise.reject(new Error('Invalid URL'));
    }
    return this.invoke<void>('open-external-url', {
      url,
      forceInBrowser: options?.forceInBrowser,
      preferMeetingApp: options?.preferMeetingApp,
    });
  }

  openSystemDateSettings(): Promise<void> {
    return this.invoke<void>('open-system-date-settings');
  }

  /**
   * Check macOS Full Disk Access status
   */
  checkFullDiskAccessPermission(): Promise<boolean> {
    return this.invoke<boolean>('check-full-disk-access-permission');
  }

  /**
   * Open the Full Disk Access pane in System Settings
   */
  openFullDiskAccessSettings(): Promise<void> {
    return this.invoke<void>('open-full-disk-access-settings');
  }

  openAccessibilitySettings(): Promise<void> {
    return this.invoke<void>('open-accessibility-settings');
  }

  openSystemAudioRecordingSettings(): Promise<void> {
    return this.invoke<void>('open-system-audio-recording-settings');
  }

  /**
   * Get all installed apps on macOS with icons - handled by Electron main process
   */
  async getInstalledApps(): Promise<InstalledApp[]> {
    return this.invoke<InstalledApp[]>('get-installed-apps');
  }

  async checkForUpdate(): Promise<UpdateResult> {
    return this.invoke<UpdateResult>(UpdateEventType.CheckForUpdates);
  }

  async getUpdateStatus(): Promise<UpdateStatus> {
    return this.invoke<UpdateStatus>(UpdateEventType.GetStatus);
  }

  async getCurrentVersionChannel(): Promise<UpdateChannel | null> {
    return this.invoke<UpdateChannel | null>(
      UpdateEventType.GetCurrentVersionChannelInfo
    );
  }
  async getUserSubscribedChannel(): Promise<UpdateChannel | null> {
    return this.invoke<UpdateChannel | null>(
      UpdateEventType.GetUserSubscribedChannel
    );
  }

  async downloadUpdate(): Promise<UpdateResult> {
    return this.invoke<UpdateResult>(UpdateEventType.StartDownload);
  }

  async installUpdate(): Promise<UpdateResult> {
    return this.invoke<UpdateResult>(UpdateEventType.QuitAndInstall);
  }

  async reportUpdateActivity(report: UpdateActivityReport): Promise<void> {
    return this.invoke<void>(UpdateEventType.ReportActivity, report);
  }

  async getInstallHealth(): Promise<InstallHealthStatus> {
    return this.invoke<InstallHealthStatus>(UpdateEventType.GetInstallHealth);
  }

  async repairInstall(): Promise<InstallRepairResult> {
    return this.invoke<InstallRepairResult>(UpdateEventType.RepairInstall);
  }

  async dismissInstallRepair(): Promise<void> {
    return this.invoke<void>(UpdateEventType.DismissInstallRepair);
  }

  async quitApp(source: QuitSource): Promise<void> {
    return this.invoke<void>('quit-app', source);
  }

  async getLogFiles(): Promise<LogFileInfo[]> {
    return this.invoke<LogFileInfo[]>('get-log-files');
  }

  async getCrashLogs(): Promise<LogFileInfo[]> {
    return this.invoke<LogFileInfo[]>('get-crash-logs');
  }

  async getContextKitCliCrashLogs(): Promise<LogFileInfo[]> {
    return this.invoke<LogFileInfo[]>('get-contextkit-cli-crash-logs');
  }

  async readFile(filePath: string): Promise<Uint8Array> {
    return this.mainProcessApi.readFile(filePath);
  }

  async readFileAsText(filePath: string): Promise<string> {
    return this.mainProcessApi.readFileAsText(filePath);
  }

  async deleteFile(filePath: string): Promise<void> {
    return this.mainProcessApi.deleteFile(filePath);
  }

  async getLatestLogFileContent(): Promise<{
    content: string;
    fileName: string;
  } | null> {
    const logFiles = await this.getLogFiles();
    const latestLogFile = head(logFiles) ?? null;
    if (!latestLogFile) return null;
    const content = await this.readFileAsText(latestLogFile.path);
    return {
      content,
      fileName: latestLogFile.name,
    };
  }
  async getLatestCrashLogContent(): Promise<{
    content: string;
    fileName: string;
  } | null> {
    const crashLogs = await this.getCrashLogs();
    const latestCrashLog = head(crashLogs) ?? null;
    if (!latestCrashLog) return null;
    const content = await this.readFileAsText(latestCrashLog.path);
    return {
      content,
      fileName: latestCrashLog.name,
    };
  }

  async getLatestContextKitCliCrashLogContent(): Promise<{
    content: string;
    fileName: string;
  } | null> {
    const crashLogs = await this.getContextKitCliCrashLogs();
    const latestCrashLog = head(crashLogs) ?? null;
    if (!latestCrashLog) return null;
    const content = await this.readFileAsText(latestCrashLog.path);
    return {
      content,
      fileName: latestCrashLog.name,
    };
  }
  async isUserActive(): Promise<boolean> {
    return this.invoke<boolean>('is-user-active');
  }

  /**
   * Get user's time format preference from OS
   * Returns true if user prefers 12hr, false if 24hr, null if no explicit preference
   */
  async getPrefer12HourTime(): Promise<boolean | null> {
    return this.invoke<boolean | null>('get-prefer-12-hour-time');
  }

  /**
   * Check if process sampling is available on this platform
   * Only available on macOS with /usr/bin/sample
   */
  async isProcessSampleAvailable(): Promise<boolean> {
    return this.invoke<boolean>('is-process-sample-available').catch(
      () => false
    );
  }

  /**
   * Capture process sample of ContextKit
   * Returns the sample file content and metadata
   */
  async captureProcessSample(): Promise<{
    filePath: string;
    fileName: string;
    fileContent: string;
    duration: number;
    pid: number;
    timestamp: string;
  }> {
    return this.invoke('capture-process-sample');
  }
}
